import {
    type TCache,
    type TMiddleware,
    type VersionToGatekeeper
} from '@casumo/fabric-fundamentals';
import type { TMarket } from '../market/types';
import { sourcedBus } from './MessageBus';
import { navigationApi } from '../navigation';
import { get, writable, type Writable } from 'svelte/store';
import type { TPlayerData } from '../lisService/types';
import { browser } from '$app/environment';
import { createOrRefreshInstance, getAuth } from '../legacy/auth';
import type { Bootstrap } from '../legacy/auth/types';
import { deviceInfo as deviceInformation } from '../legacy/device';
import { memoize } from 'lodash-es';
import { removeMarketFromUrl } from '../helpers/utils';
import { getRouteAbsolutePrefix } from '../navigation/getRouteAbsolutePrefix';
import { type TRouteId } from '$src/generated/routeIds';
import { toggleFullScreen } from '../fullscreen';
import { goto } from '$app/navigation';
import { cleanUpCacheHeaders, getCacheHeadersValues } from './utils';
import type { BaseCache } from '$lib/services/cache/BaseCache';
import { IndexedDBCache } from '$lib/services/cache/indexedDBCache';
import { InMemoryCache } from '$lib/services/cache/inMemoryCache';
import { swrFetch } from '$lib/services/swr';

export const GATEKEEPER_VERSION: keyof VersionToGatekeeper = 'v2.2.9';

// No need to support all versions
export type ReactGateKeeper = VersionToGatekeeper[typeof GATEKEEPER_VERSION];

export type TSpineGatekeeperFullContext = {
    market: TMarket;
    country: string | null;
    player: TPlayerData | undefined;
    sessionId: string | undefined;
    bypassCache: boolean;
    authBootstrap: Bootstrap;
    url: URL | null;
    fetch: typeof global.fetch;
    noScroll: Writable<boolean>;
};
export type TSpineGatekeeperUpdaterContext = Omit<
    TSpineGatekeeperFullContext,
    'authBootstrap' | 'url' | 'fetch' | 'noScroll'
>;

type TGetGatekeeperFetcher = (
    platformFetch: typeof global.fetch,
    options: {
        cache: BaseCache;
        bypassCache?: boolean;
    }
) => typeof global.fetch;

const getGatekeeperFetcher: TGetGatekeeperFetcher =
    (platformFetch: typeof global.fetch, { cache, bypassCache = false }) =>
    async (input: RequestInfo | URL, init?: RequestInit) => {
        const headers = new Headers(
            (input instanceof Request ? input.headers : init?.headers) || {}
        );

        const { key, ttl, freshness, tags } = getCacheHeadersValues(headers);
        cleanUpCacheHeaders(headers);

        function fetcher(): Promise<Response> {
            const request =
                input instanceof Request
                    ? platformFetch(
                          new Request(input, { ...init, credentials: 'include', headers })
                      )
                    : platformFetch(input, { ...init, credentials: 'include', headers });

            return request;
        }

        return swrFetch({
            fetcher,
            cache,
            key,
            ttl,
            freshness,
            tags,
            bypassCache
        });
    };

export function initGatekeeper({
    authBootstrap,
    url,
    fetch,
    noScroll,
    ...init
}: TSpineGatekeeperFullContext) {
    // SSR BYPASS - we don't really need to do anything for SSR as gatekeeper is used by mfes
    if (!browser) return () => {};

    const cache = window.indexedDB ? new IndexedDBCache() : new InMemoryCache();

    const middlewares: Record<
        TMiddleware['context'],
        Record<TMiddleware['key'], TMiddleware['middleware']>
    > = {
        logout: {}
    };

    const spineContext = writable<TSpineGatekeeperUpdaterContext>(init);
    const getSessionId = () => get(spineContext).sessionId;
    const getPlayerData = () => get(spineContext).player;
    const getMarket = () => get(spineContext).market;
    const getCountry = () => get(spineContext).country;
    const getBypassCache = () => get(spineContext).bypassCache;
    const commandLogout = async () => {
        if (!getSessionId()) return true;
        const ENDPOINT = `/api/common/command/logout`;
        const response = await fetch(ENDPOINT, { method: 'POST', credentials: 'include' });

        if (response.ok) {
            return true;
        }
        return false;
    };

    const auth: ReactGateKeeper['auth'] = {
        logIn: async (options?: { redirectUrl?: string }) => (await getAuth()).logIn(options),
        logOut: async () => {
            const middleware = middlewares.logout;

            for (const key in middleware) {
                if (middleware[key]) {
                    try {
                        console.log('Running middleware', key);
                        await middleware[key]();
                    } catch (error) {
                        console.error('Middleware error', error);
                        return;
                    }
                }
            }
            middlewares.logout = {};

            await Promise.all([getAuth().then((i) => i.logOut()), commandLogout()]);
        },
        isLoggedIn: async () => Boolean(getSessionId()),
        getAccessToken: async () => (await getAuth()).getTokenSet()?.accessToken?.token,
        getCasumoName: async () => getPlayerData()?.casumoName,
        getName: async () => {
            const playerData = getPlayerData();
            if (!playerData) return undefined;
            const { firstName, lastName } = playerData.contactInfo.name;
            return `${firstName} ${lastName}`;
        },
        getSessionId: async () => getSessionId(),
        getPlayerId: async () => getPlayerData()?.playerId,
        getPlayerEmail: async () => getPlayerData()?.contactInfo.email
    };
    const wallet: ReactGateKeeper['wallet'] = {
        getPlayerCurrency: async () => getPlayerData()?.wallet?.balance?.iso4217CurrencyCode
    };
    type TNavigateParams = Parameters<ReactGateKeeper['navigation']['navigate']>[1];

    const navigation: ReactGateKeeper['navigation'] = {
        ...navigationApi,
        navigate: (...args: [string, TNavigateParams]) =>
            navigationApi.navigate(...(args as [TRouteId, TNavigateParams])),
        isActiveRoute: (routeId, params) =>
            get(navigationApi.isActiveRoute(routeId as TRouteId, params)),
        getRoute: (routeId, params = {}, options = {}) => {
            const foundRoute = navigationApi.getRoute(routeId as TRouteId, params);
            if (!foundRoute) return null;
            const route = options?.ignoreMarketPrefix
                ? removeMarketFromUrl(foundRoute)
                : foundRoute;
            const prefix =
                getRouteAbsolutePrefix(url || new URL(window.location.href), options) ?? '';
            return prefix + route;
        },
        goBack: ({ fallbackRouteId, opts = {} } = {}) => {
            const inNewTab = window.history.length === 1;
            const fromDifferentOrigin =
                navigationApi.getPreviousUrl()?.origin !== window.location.origin;

            if (fallbackRouteId && (inNewTab || fromDifferentOrigin)) {
                const fallback = navigationApi.getRoute(fallbackRouteId as TRouteId);
                if (fallback) {
                    goto(fallback, opts);
                    return;
                }
            }

            window.history.back();
        }
    };
    const isNative: ReactGateKeeper['isNative'] = (returnProps?: boolean) => {
        if (!returnProps) {
            return Boolean(window.native);
        }

        return (window.native as any) || false; // eslint-disable-line @typescript-eslint/no-explicit-any
    };
    // TODO: Resolve type between spine and Fabric Fundamentals the right way
    const localisation: ReactGateKeeper['localisation'] = {
        // @ts-expect-error mismatch on expected types
        get marketSlug() {
            return getMarket().slug;
        },
        // @ts-expect-error mismatch on expected types
        get localeCode() {
            return getMarket().locale;
        },
        get jurisdiction() {
            return getMarket().jurisdiction;
        },
        get country() {
            return getCountry() || '__';
        },
        // TODO: Remove this once all mfe have transitioned from userCountry to country
        get userCountry() {
            return getCountry();
        },
        get userTimeZone() {
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
        }
    };
    const deviceInfo: ReactGateKeeper['deviceInfo'] = {
        get deviceId() {
            return deviceInformation.getDeviceId();
        }
    };

    const ui = {
        toggleFullScreen,
        get isFullScreen() {
            return browser && !!document.fullscreenElement;
        },
        disableScroll() {
            noScroll.set(true);
        },
        enableScroll() {
            noScroll.set(false);
        }
    };

    const middleware: ReactGateKeeper['middleware'] = {
        add: (definition) => {
            if (!definition.context || !definition.key || !definition.middleware) {
                const missingDefinitions = [] as string[];

                if (!definition.context) missingDefinitions.push('context');
                if (!definition.key) missingDefinitions.push('key');
                if (!definition.middleware) missingDefinitions.push('middleware');

                console.error(`Middleware definition is missing ${missingDefinitions.join(', ')}`);

                return;
            }

            const context = definition.context;

            if (!middlewares[context]) {
                middlewares[context] = {
                    [definition.key]: definition.middleware
                };
            } else {
                middlewares[context][definition.key] = definition.middleware;
            }
            return;
        },
        remove: (context, key) => {
            if (middlewares[context][key]) {
                delete middlewares[context][key];
            }
            return;
        }
    };

    const cacheWithProxy: TCache = {
        getCache() {
            return new Proxy(cache, {
                get(target, prop, receiver) {
                    const value = Reflect.get(target, prop, receiver);
                    console.warn(
                        'If you are using getCache programatically, you are not using it correctly.',
                        'it should be used for debugging purposes only.'
                    );
                    return value;
                }
            });
        },
        invalidateAll: cache.invalidateAll,
        invalidateByKey: cache.invalidateByKey,
        invalidateByTag: cache.invalidateByTag
    };

    const staticGatekeeper: Omit<ReactGateKeeper, 'messageBus'> = {
        navigation,
        wallet,
        auth,
        isNative,
        deviceInfo,
        fetch: getGatekeeperFetcher(fetch, { cache, bypassCache: getBypassCache() }),
        cache: cacheWithProxy,
        localisation,
        mode: 'legacy',
        middleware,
        ui
    };

    // TODO: Investigate if this needs to be reactive.
    createOrRefreshInstance(
        authBootstrap,
        init.market.slug,
        url || new URL(window.location.href),
        fetch
    );

    // @ts-expect-error We do not support full gatekeeper versions on SPINE
    window.fabric.getGatekeeper ||= memoize(
        (_version: typeof GATEKEEPER_VERSION = GATEKEEPER_VERSION, name: string) => ({
            messageBus: sourcedBus(name),
            ...staticGatekeeper
        }),
        (version, name) => `gk:${String(version)}@${name}`
    );

    const update = (newCtx: TSpineGatekeeperUpdaterContext) => {
        spineContext.set(newCtx);
    };
    return update;
}
